import { Routes, Route, useLocation } from "react-router-dom";
import Layout from "./layout/Layout";
import LoginPage from "./pages/login";
import ProtectedRoute from "./components/ProtectedRoute";
import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import WrongPortalPage from "./pages/wrongPortal";
import NotFound from "../../../packages/ui/src/pages/notFound";
import {
  ADMIN_PUBLIC_ROUTES,
  ADMIN_PROTECTED_ROUTES,
  isPublicRoute,
  isNotFoundRoute,
} from "./routes";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { setGlobalState, logout } from "./store/auth";
import { store } from "./store";
import { useQueryClient } from "@tanstack/react-query";
import Dashboard from "./pages/Dashboard";
// Import pages from admin-surgia
import Catalog from "@admin-surgia/pages/Catalog";
import Users from "@admin-surgia/pages/Users";
import UserDetails from "@admin-surgia/pages/UserDetails";
import Tenants from "@admin-surgia/pages/Tenants";
import TenantDetails from "@admin-surgia/pages/TenantDetails";
import PurchaseOrders from "@admin-surgia/pages/PurchaseOrders";
import PurchaseOrderDetailsView from "@admin-surgia/pages/PurchaseOrderDetailsView";
import ProductAssignment from "@admin-surgia/pages/ProductAssignment/steps/productSelection";
import ProductDetails from "@admin-surgia/pages/ProductDetails";

function App() {
  const location = useLocation();
  const auth = useAuth();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const pendingAuthProcessedRef = useRef(false);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnlineStatusChange = () => {
      if (navigator.onLine) {
        toast.success("You are back online!", {
          duration: 3000,
        });
      } else {
        toast.error("You are offline. Please check your internet connection.", {
          duration: 5000,
        });
      }
    };

    window.addEventListener("online", handleOnlineStatusChange);
    window.addEventListener("offline", handleOnlineStatusChange);

    return () => {
      window.removeEventListener("online", handleOnlineStatusChange);
      window.removeEventListener("offline", handleOnlineStatusChange);
    };
  }, []);

  useEffect(() => {
    const handleAuthLogout = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log("🚨 [APP] 401 LOGOUT EVENT DETECTED!", customEvent.detail);
      queryClient.cancelQueries();
      queryClient.clear();
      localStorage.clear();
      sessionStorage.clear();
      store.dispatch(logout());
      auth?.removeUser();
      auth.signoutRedirect();
    };

    window.addEventListener("auth:logout", handleAuthLogout);
    return () => {
      window.removeEventListener("auth:logout", handleAuthLogout);
    };
  }, [auth, queryClient]);

  // Handle pending auth data from OIDC callback
  useEffect(() => {
    if (pendingAuthProcessedRef.current) {
      return;
    }

    const pendingAuthData = sessionStorage.getItem("pendingAuthData");

    if (pendingAuthData) {
      try {
        const authData = JSON.parse(pendingAuthData);
        
        if (!authData.userInfo?.subject_id) {
          console.error("❌ [APP] pendingAuthData has no subject_id - SKIPPING");
          sessionStorage.removeItem("pendingAuthData");
          pendingAuthProcessedRef.current = true;
          return;
        }
        
        if (authData.userInfo) {
          const userInfo = authData.userInfo;

          dispatch(
            setGlobalState({
              first_name: userInfo.first_name || null,
              last_name: userInfo.last_name || null,
              full_name: userInfo.full_name || null,
              email: userInfo.email || null,
              subject_id: userInfo.subject_id || null,
              creation_date: userInfo.creation_date || null,
              user_permissions: userInfo.user_permissions || [],
              user_branches: userInfo.user_branches
                ? userInfo.user_branches
                    .map((branch: { id: number; name: string }) => branch.id)
                    .join(",")
                : "",
              access_token: authData.access_token || "",
              id_token: authData.id_token || "",
              refresh_token: authData.refresh_token || "",
              expires_at: authData.expires_at || 0,
            })
          );
          sessionStorage.removeItem("pendingAuthData");
          pendingAuthProcessedRef.current = true;
        }
      } catch (error) {
        console.error("Error parsing pending auth data:", error);
      }
    }
  }, [dispatch]);

  // Broadcast channel listener for cross-tab auth sync
  useEffect(() => {
    const authChannel = new BroadcastChannel("auth-sync");

    authChannel.onmessage = (event) => {
      const { type, data } = event.data;

      switch (type) {
        case "LOGOUT":
          dispatch(logout());
          auth.removeUser();
          break;
        case "LOGIN":
          dispatch(setGlobalState(data));
          break;
        case "TOKEN_REFRESH":
          localStorage.setItem("token", data.access_token);
          break;
      }
    };

    return () => {
      authChannel.close();
    };
  }, [dispatch, auth]);

  // If current path is a public route - render without layout
  if (isPublicRoute(location.pathname) || isNotFoundRoute(location.pathname)) {
    return (
      <Routes>
        <Route path={ADMIN_PUBLIC_ROUTES.LOGIN} element={<LoginPage />} />
        <Route
          path={ADMIN_PUBLIC_ROUTES.WRONG_PORTAL}
          element={<WrongPortalPage />}
        />
        <Route path={ADMIN_PUBLIC_ROUTES.NOT_FOUND} element={<NotFound />} />
      </Routes>
    );
  }

  // Protected routes that need authentication - render with layout
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route
          path={ADMIN_PROTECTED_ROUTES.HOME}
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.CATALOG}
          element={
            <ProtectedRoute>
              <Catalog />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.USERS}
          element={
            <ProtectedRoute>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.DASHBOARD}
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.TENANTS}
          element={
            <ProtectedRoute>
              <Tenants />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.PURCHASE_ORDERS}
          element={
            <ProtectedRoute>
              <PurchaseOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.TENANT_DETAILS}
          element={
            <ProtectedRoute>
              <TenantDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.TENANT_DETAILS_PURCHASE_ORDER_DETAILS}
          element={
            <ProtectedRoute>
              <PurchaseOrderDetailsView />
            </ProtectedRoute>
          }
        />
        <Route
          path={ADMIN_PROTECTED_ROUTES.TENANT_DETAILS_USER_DETAILS}
          element={
            <ProtectedRoute>
              <UserDetails />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path={ADMIN_PROTECTED_ROUTES.PRODUCT_ASSIGNMENT}
        element={
          <ProtectedRoute>
            <ProductAssignment />
          </ProtectedRoute>
        }
      />
      <Route
        path={ADMIN_PROTECTED_ROUTES.PRODUCT_DETAILS}
        element={
          <ProtectedRoute>
            <ProductDetails />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;

